import React, { useEffect, useState } from 'react';
import { Alert, Box, Divider, FormControlLabel, InputAdornment, Paper, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material';
import { getUsersAPI, putVisitorsNotificationAPI } from 'apis/settings/settingsAPI';
import VisitorPopup from 'components/menu/UserManagementPopup/VisitorPopup';
import { DataGrid, GridColDef, GridLocaleText } from '@mui/x-data-grid';
import { enUS, jaJP, koKR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

interface User {
  id: number;
  username: string;
  status?: string;
  name: string;
  type: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  companyPhoneNumber?: string;
  penaltyCount?: number;
  groupName?: string; //임시
}

const locales: { [key: string]: Partial<GridLocaleText> } = {
  ko: koKR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
  ja: jaJP.components.MuiDataGrid.defaultProps.localeText
};

export default function UserManagement() {
  const [externalUsers, setExternalUsers] = useState<User[]>([]); // 외부 사용자 상태
  const [visitorNotification, setVisitorNotification] = useState(''); // 외부사용자 알림
  const [notificationMessage, setNotificationMessage] = useState('');
  const [openVisitorPopup, setOpenVisitorPopup] = useState(false);
  // 클릭한 방문자 정보
  const [selectedVisitor, setSelectedVisitor] = useState<User | null>(null);
  const [filterText, setFilterText] = useState('');
  const [filteredExternalUsers, setFilteredExternalUsers] = useState<User[]>([]);

  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();

  const handleVisitorNotificationChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value; // 선택된 value 값 가져오기
    const data = {
      notificationType: selectedValue // 외부사용자 알림 변경 알림 타입 (email 또는 phone)
    };

    try {
      const response = await putVisitorsNotificationAPI(data).then(() => {
        setVisitorNotification(selectedValue);
        setNotificationMessage('저장하였습니다.');
        setTimeout(() => {
          setNotificationMessage('');
        }, 2000);
      }); // API 호출
      // console.log('알림 타입 변경 성공:', response);
    } catch (error) {
      // console.error('알림 타입 변경 실패:', error);
      console.error(error);
    }
  };

  // 방문자 팝업 열고 닫는 함수
  const handleOpenVisitorPopup = (visitorId: any) => {
    // console.log('visitorId', visitorId);
    // console.log('externalUsers', externalUsers);
    const visitorData = externalUsers.find((externalUser) => externalUser.id === visitorId) || null;
    // console.log('visitorData', visitorData);
    setSelectedVisitor(visitorData); // employeeData가 undefined일 경우 null로
    // setSelectedVisitor(visitorInfo);
    setOpenVisitorPopup(true);
  };

  const handleCloseVisitorPopup = async () => {
    setSelectedVisitor(null); // 선택된 사용자 초기화
    setOpenVisitorPopup(false);

    await handleRefresh();
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const handleRefresh = async () => {
    try {
      const response = await getUsersAPI(); // API 호출
      // console.log('사용자관리 API response : ', response);
      // console.log('사용자관리 API response - 방문자 : ', response.visitors);
      // 데이터를 있으면 외부,내부 직원 데이터를 업데이트
      if (response) {
        setVisitorNotification(response.visitorNotificationType || '');
        setExternalUsers(response.visitors || []); // 외부 사용자 데이터 저장
      }
    } catch (error) {
      // console.error('사용자 데이터 가져오기 실패:', error);
      console.error(error);
    }
  };

  const columns: GridColDef[] = [
    { field: 'username', headerName: t('account_management.chart.id'), flex: 1 },
    {
      field: 'type',
      headerName: t('account_management.chart.national_foreigner'),
      flex: 1
      // valueGetter: (params) => (params === '내국인' ? t('account_management.popup.national') : t('account_management.popup.foreigner'))
    },
    { field: 'name', headerName: t('account_management.chart.name'), flex: 1 },
    { field: 'companyName', headerName: t('account_management.chart.company_name'), flex: 1 },
    { field: 'email', headerName: t('account_management.chart.email'), flex: 1.5 },
    { field: 'phoneNumber', headerName: t('account_management.chart.phone'), flex: 1 }
  ];
  const fetchUsers = async () => {
    try {
      const response = await getUsersAPI(); // API 호출
      // console.log('사용자관리 API response : ', response);
      // console.log('사용자관리 API response - 방문자 : ', response.visitors);
      // 데이터를 있으면 외부,내부 직원 데이터를 업데이트
      if (response) {
        setVisitorNotification(response.visitorNotificationType || '');
        setExternalUsers(response.visitors || []); // 외부 사용자 데이터 저장
      }
    } catch (error) {
      // console.error('사용자 데이터 가져오기 실패:', error);
      console.error(error);
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    fetchUsers(); // 컴포넌트 마운트 시 데이터 가져오기

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // 빈 배열로 의존성 배열을 설정하여 처음 한 번만 실행

  useEffect(() => {
    const test =       externalUsers.filter(
        (externalUser) =>
          externalUser.username.toLowerCase().includes(filterText.toLowerCase()) || externalUser.name.toLowerCase().includes(filterText.toLowerCase())
      );
      console.log(test);
    setFilteredExternalUsers(
      externalUsers.filter(
        (externalUser) =>
          externalUser.username.toLowerCase().includes(filterText.toLowerCase()) || externalUser.name.toLowerCase().includes(filterText.toLowerCase())
      )
    );
  }, [filterText, externalUsers]);

  return (
    <Box padding={2} display="flex" flexDirection="column" gap={2}>
      <Box
        // padding={2}
        // paddingBottom={2}
        borderRadius={1}
        // bgcolor={theme.palette.contentBlock.background}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('account_management.title')}
        </Typography>

        <Divider flexItem />
      </Box>

      {/* 외부 사용자 알림 */}
      <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
        <Typography variant="h5" fontWeight="bold">
          {t('account_management.external_user_notification')}
        </Typography>

        {notificationMessage && <Alert severity="success">{notificationMessage}</Alert>}
        <RadioGroup
          row
          name="notification"
          value={visitorNotification}
          onChange={handleVisitorNotificationChange}
          sx={{ borderRadius: 1, bgcolor: theme.palette.mode === 'dark' ? '#303030' : '#ffffff', boxShadow: 1, padding: 1, paddingX: 2 }}
        >
          <FormControlLabel value="EMAIL" control={<Radio size="small" />} label={t('account_management.chart.email')} />
          <FormControlLabel value="PHONE" control={<Radio size="small" />} label={t('account_management.chart.phone')} />
        </RadioGroup>
      </Box>

      {/* 외부 사용자 테이블 */}
      <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight="bold">
            {t('account_management.external_user')}
          </Typography>
          <TextField
            value={filterText}
            size="small"
            onChange={handleFilterChange}
            placeholder={t('account_management.search_placeholder')}
            sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'none' : '#ffffff', height: 29, width: 150 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ marginLeft: -1, marginRight: '-12px' }} />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>

        <Paper sx={{ height: 'calc(100vh - 496px)', minHeight: 400 }}>
          {/* <Paper sx={{ height: 'calc(100vh - 496px)' }}></Paper> */}
          <DataGrid
            density="compact"
            columnHeaderHeight={60}
            rows={filteredExternalUsers}
            columns={columns}
            getRowId={(row) => row.id}
            disableColumnMenu
            disableColumnSelector
            disableRowSelectionOnClick
            showCellVerticalBorder
            onRowClick={(params) => handleOpenVisitorPopup(params.id as number)}
            localeText={locales[language]}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.table.header
              },
              '& .MuiDataGrid-columnSeparator': {
                color: theme.palette.table.border
              },
              '& .MuiDataGrid-cell': {
                cursor: 'pointer',
                borderRightColor: theme.palette.table.border,
                backgroundColor: theme.palette.table.row
              }
            }}
          />
        </Paper>
      </Box>

      {/* 방문자 클릭시 뜨는  Popup */}
      {openVisitorPopup && <VisitorPopup isOpen={openVisitorPopup} handleClose={handleCloseVisitorPopup} visitorData={selectedVisitor} />}
    </Box>
  );
}
